import React, { useEffect, useReducer, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import translate from '../../../commonComponents/translate';
import Modal from '../../../commonComponents/modal/Modal';
import PaymentStep from '../../signIn/modals/SubscriptionPlanModal/steps/PaymentStep';
import { PAYMENT_METHOD } from '../../../services/utilityFunctions';
import Notifications from '../../../services/notifications';
import SubscriptionProp from '../propTypes/subscriptionProp';
import { getCreditCardList } from '../../../services/checkoutHelper';
import Loader from '../../../services/loader';

const initialStatePayment = {
  selectedCreditCard: null,
  userCreditCards: [],
};

export const paymentReducer = (state, action) => {
  switch (action.type) {
    case 'updatedUserCards': {
      return {
        ...state,
        userCreditCards: action.payload.cards,
        addingNewCard: false,
      };
    }
    case 'removeCreditCard': {
      const { cards } = action.payload;
      let returnedState = {
        ...state,
        userCreditCards: cards,
        addingNewCard: false,
      };
      const cardStillExist = cards.find(
        c => c._id === state.selectedCreditCard,
      );
      if (!cardStillExist) {
        returnedState = {
          ...returnedState,
          selectedCreditCard: null,
        };
      }
      return returnedState;
    }
    case 'updatedCardSelected': {
      return {
        ...state,
        selectedCreditCard: action.payload.cardId,
      };
    }
    case 'updateCards': {
      return {
        ...state,
        userCreditCards: action.payload.cards,
      };
    }
    default:
      console.log('ERROR - Payment Reducer');
  }
  return null;
};

const UpdatePaymentModal = ({
  t,
  onClose,
  _csrf,
  tenant,
  subscription,
  setPaymentMethodId,
  isFromPlan,
}) => {
  const loader = useRef(new Loader('#payment-method-update'));
  const [isLoadingModal, setIsLoadingModal] = useState(true);
  const [isLoadingButtons, setIsLoadingButtons] = useState(false);

  useEffect(() => {
    if (loader.current) {
      if (isLoadingModal) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isLoadingModal]);

  const [planPayment, dispatchPlanPayment] = useReducer(
    paymentReducer,
    initialStatePayment,
    () => ({
      ...initialStatePayment,
      tabActive: PAYMENT_METHOD.CREDIT_CARD,
      selectedCreditCard: subscription?.paymentMethodId || null,
    }),
  );

  const { tabActive, selectedCreditCard, userCreditCards } = planPayment;
  const updateMethodPayment = () => {
    setIsLoadingButtons(true);
    axios({
      url: `/api/market/order/subscription/${subscription._id}/update`,
      method: 'PUT',
      data: {
        paymentMethodId: selectedCreditCard,
        paymentMethodType: PAYMENT_METHOD.CREDIT_CARD,
      },
      headers: {
        'Accept-Version': '1.0.0',
        'X-Gamification-API': 'Games',
        'X-CSRF-Token': _csrf,
        _csrf,
      },
    })
      .then(() => {
        Notifications.showNotificationSuccess(
          t('success'),
          t('order.subscription.paymentMethod.success'),
        );
        onClose();
        if (!isFromPlan) {
          window.location.reload();
        }
        setIsLoadingButtons(false);
      })
      .catch(err => {
        setIsLoadingButtons(false);
        Notifications.showErrorFromRequest(
          err,
          'order.subscription.paymentMethod.error',
        );
        console.log(err);
      });
  };

  const updateMethodPaymentOnPlan = () => {
    setIsLoadingButtons(true);
    axios({
      url: '/api/subscription/subscribe',
      method: 'POST',
      data: {
        paymentMethodId: selectedCreditCard,
        currentUserPlan: subscription.currentUserPlan,
      },
      headers: {
        'Accept-Version': '1.0.0',
        'X-Gamification-API': 'License',
        'X-CSRF-Token': _csrf,
        _csrf,
      },
    })
      .then(() => {
        Notifications.showNotificationSuccess(
          t('success'),
          t('order.subscription.paymentMethod.success'),
        );
        window.location.reload();
      })
      .catch(err => {
        setIsLoadingButtons(false);
        Notifications.showErrorFromRequest(
          err,
          'order.subscription.paymentMethod.error',
        );
        console.log(err);
      });
  };

  useEffect(() => {
    try {
      setIsLoadingModal(true);
      const getCardsList = async () => {
        const cardsList = await getCreditCardList(_csrf, true, true);
        dispatchPlanPayment({
          type: 'updatedUserCards',
          payload: { cards: cardsList },
        });
      };
      getCardsList();
      setIsLoadingModal(false);
    } catch (err) {
      Notifications.showErrorFromRequest(err, t('error_fetchCards'));
      setIsLoadingModal(false);
    }
  }, [dispatchPlanPayment, _csrf, setIsLoadingModal]);

  const updatePaymentMethod = (type, payload) => {
    dispatchPlanPayment({ type, payload });
  };

  return (
    <Modal onClose={onClose} id="payment-method-update">
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        role="document"
      >
        <div className="modal-content">
          {onClose && (
            <button
              type="button"
              className="close close-modal hide-inSmall"
              onClick={onClose}
              disabled={isLoadingButtons}
            >
              <span aria-hidden="true">×</span>
            </button>
          )}
          <div className="modal-body">
            <div className="add-new-payment-method">
              <div id="yourPaymentMethod">
                <PaymentStep
                  _csrf={_csrf}
                  tenant={tenant}
                  setPaymentMethodId={setPaymentMethodId}
                  handleIsLoading={() => {}}
                  userCreditCards={userCreditCards}
                  tabActive={tabActive}
                  updatePaymentMethod={updatePaymentMethod}
                  selectedCreditCard={selectedCreditCard}
                  showSaveCard={false}
                  currency={subscription?.currentUserPlan?.currency}
                  hidePaypal
                />
                <br />
                <div className="handle-form-address">
                  <button
                    type="button"
                    className="secondary"
                    onClick={onClose}
                    disabled={isLoadingButtons}
                  >
                    {t('back')}
                  </button>
                  <button
                    type="button"
                    className={`btn btn-dynamic primary ${
                      isLoadingButtons &&
                      'm-loader m-loader--right m-loader--light'
                    }`}
                    disabled={isLoadingButtons}
                    onClick={
                      isFromPlan
                        ? updateMethodPaymentOnPlan
                        : updateMethodPayment
                    }
                  >
                    {t('save')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

UpdatePaymentModal.propTypes = {
  t: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  tenant: PropTypes.shape({}).isRequired,
  setPaymentMethodId: PropTypes.func.isRequired,
  subscription: PropTypes.shape(SubscriptionProp.propType).isRequired,
  isFromPlan: PropTypes.bool,
};

UpdatePaymentModal.defaultProps = {
  isFromPlan: false,
};

export default translate(UpdatePaymentModal);
